/**
 * Connector Setup Modal
 * Multi-step form: Select Type → Credentials → Config → Review
 * Credential fields rendered dynamically from ConnectorTypeDefinition
 */

import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';

const STEPS = ['Select Type', 'Credentials', 'Configuration', 'Review'];

const ConnectorSetupModal = ({ isOpen, onClose, onSave, connectorTypes = [], existingConnectors = [], preselectedType = null }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const defaultStartDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [form, setForm] = useState({
    connector_type: '',
    display_name: '',
    credentials: {},
    config: {},
    poll_interval_minutes: 15,
    batch_size: 100,
    lookback_days: 7,
    start_date: defaultStartDate,
    activate: false,
  });

  const selectedType = connectorTypes.find(t => t.connector_type === form.connector_type);
  const alreadyConfigured = existingConnectors.map(c => c.connector_type);
  const isPushOnly = selectedType?.connector_type === 'audio_upload';

  useEffect(() => {
    if (isOpen) {
      const matched = preselectedType
        ? connectorTypes.find(t => t.connector_type === preselectedType)
        : null;

      if (matched) {
        const defaults = {};
        matched.credential_fields.forEach(f => { defaults[f.name] = ''; });
        setForm({
          connector_type: matched.connector_type,
          display_name: matched.display_name,
          credentials: defaults,
          config: matched.default_config || {},
          poll_interval_minutes: 15,
          batch_size: 100,
          lookback_days: 7,
          start_date: defaultStartDate,
          activate: false,
        });
        setStep(1);
      } else {
        setForm({
          connector_type: '',
          display_name: '',
          credentials: {},
          config: {},
          poll_interval_minutes: 15,
          batch_size: 100,
          lookback_days: 7,
          start_date: defaultStartDate,
          activate: false,
        });
        setStep(0);
      }
    }
  }, [isOpen, preselectedType, connectorTypes]);

  if (!isOpen) return null;

  const handleTypeSelect = (type) => {
    const defaults = {};
    type.credential_fields.forEach(f => { defaults[f.name] = ''; });
    setForm(prev => ({
      ...prev,
      connector_type: type.connector_type,
      display_name: type.display_name,
      credentials: defaults,
      config: type.default_config || {},
    }));
  };

  const handleCredentialChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      credentials: { ...prev.credentials, [field]: value },
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload = {
      ...form,
      config: { ...form.config, start_date: form.start_date },
    };
    await onSave(payload);
    setSaving(false);
  };

  const canProceed = () => {
    if (step === 0) return !!form.connector_type && !alreadyConfigured.includes(form.connector_type);
    if (step === 1) {
      if (!selectedType) return false;
      return selectedType.credential_fields
        .filter(f => f.required)
        .every(f => form.credentials[f.name]?.trim());
    }
    if (step === 2) return form.display_name.trim().length > 0;
    return true;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Add Connector</h3>
            <div className="flex items-center mt-2 space-x-2">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    i === step ? 'bg-primary-100 text-primary-700 font-medium' :
                    i < step ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>{s}</span>
                  {i < STEPS.length - 1 && <ChevronRight size={12} className="text-gray-300 mx-1" />}
                </div>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5">
          {/* Step 0: Select Type */}
          {step === 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-4">Select the external system you want to connect.</p>
              <div className="grid grid-cols-2 gap-3">
                {connectorTypes.map(type => {
                  const isConfigured = alreadyConfigured.includes(type.connector_type);
                  const isSelected = form.connector_type === type.connector_type;
                  return (
                    <button
                      key={type.connector_type}
                      onClick={() => handleTypeSelect(type)}
                      className={`text-left p-4 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-200'
                          : isConfigured
                            ? 'border-gray-200 bg-gray-50 opacity-60'
                            : 'border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">{type.display_name}</h4>
                        {isConfigured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">Connected</span>}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{type.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {type.supported_interaction_types.map(t => (
                          <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">{t}</span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>

              {form.connector_type && alreadyConfigured.includes(form.connector_type) && (
                <p className="mt-3 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                  This connector type is already configured. Select a different type to continue.
                </p>
              )}
            </div>
          )}

          {/* Step 1: Credentials */}
          {step === 1 && selectedType && (
            <div className="space-y-4">
              {selectedType.credential_fields.length === 0 ? (
                <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                  {selectedType.display_name} does not require any credentials. Click Next to continue.
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-500">Enter your {selectedType.display_name} credentials.</p>
                  {selectedType.credential_fields.map(field => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      {field.description && <p className="text-[10px] text-gray-400 mb-1">{field.description}</p>}
                      <input
                        type={field.field_type === 'password' ? 'password' : 'text'}
                        placeholder={field.placeholder}
                        value={form.credentials[field.name] || ''}
                        onChange={(e) => handleCredentialChange(field.name, e.target.value)}
                        className="input text-sm"
                        required={field.required}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Step 2: Configuration */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                {isPushOnly
                  ? 'Name this connector and activate it to start accepting uploads.'
                  : 'Configure sync settings.'}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setForm(prev => ({ ...prev, display_name: e.target.value }))}
                  className="input text-sm"
                  required
                />
              </div>
              {!isPushOnly && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Poll Interval (min)</label>
                      <input
                        type="number"
                        min={1} max={1440}
                        value={form.poll_interval_minutes}
                        onChange={(e) => setForm(prev => ({ ...prev, poll_interval_minutes: parseInt(e.target.value) || 15 }))}
                        className="input text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Batch Size</label>
                      <input
                        type="number"
                        min={10} max={1000}
                        value={form.batch_size}
                        onChange={(e) => setForm(prev => ({ ...prev, batch_size: parseInt(e.target.value) || 100 }))}
                        className="input text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={form.start_date}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                      className="input text-sm"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Import interactions from this date onward.</p>
                  </div>
                </>
              )}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="activate"
                  checked={form.activate}
                  onChange={(e) => setForm(prev => ({ ...prev, activate: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <label htmlFor="activate" className="text-sm text-gray-700">Activate immediately after creation</label>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && selectedType && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Review your connector configuration.</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium text-gray-900">{selectedType.display_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium text-gray-900">{form.display_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Poll Interval</span>
                  <span className="text-gray-700">{form.poll_interval_minutes} min</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Batch Size</span>
                  <span className="text-gray-700">{form.batch_size}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Start Date</span>
                  <span className="text-gray-700">{form.start_date || 'Last 7 days'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Auto-activate</span>
                  <span className="text-gray-700">{form.activate ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Credentials</span>
                  <span className="text-green-600 font-medium">Provided</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <div>
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800"
              >
                <ChevronLeft size={14} />
                <span>Back</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            {step < 3 ? (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-50 flex items-center space-x-2"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                <span>{saving ? 'Creating...' : 'Create Connector'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectorSetupModal;
